import {
  alpha,
  Avatar,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { useGetCurrentUser } from '@api/auth/auth';
import { LogOut, Settings } from 'lucide-react';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHandleLogout } from '@/pages/common/Logout/hooks';
import ThemeModeToggle from '@/components/ThemeModeToggle';
import UploadsSidebarButton from './UploadSidebarButton';
import {
  getHeaderBackground,
  headerActionSx,
  headerFocusRing,
} from '../headerStyles';

const DashboardShellHeader = () => {
  const navigate = useNavigate();
  const userLogout = useHandleLogout();
  const { data } = useGetCurrentUser();

  const handleLogout = useCallback(async () => {
    await userLogout();
  }, [userLogout]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAccountClick = () => {
    setAnchorEl(null);
    navigate('/account');
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
      <UploadsSidebarButton />

      <ThemeModeToggle onBrandSurface sx={headerActionSx} />

      {data && (
        <>
          <Divider
            orientation="vertical"
            flexItem
            sx={(theme) => ({
              display: { xs: 'none', sm: 'block' },
              alignSelf: 'center',
              height: 24,
              mx: 1,
              borderColor: alpha(theme.palette.common.white, 0.24),
            })}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              minWidth: 0,
            }}
          >
            <Typography
              variant="body2"
              noWrap
              sx={{
                display: { xs: 'none', sm: 'block' },
                maxWidth: 220,
                fontWeight: 600,
                color: 'inherit',
              }}
            >
              Welcome, {data.first_name}!
            </Typography>
            <Tooltip title="Account settings">
              <IconButton
                onClick={handleMenuOpen}
                color="inherit"
                aria-label="Account menu"
                aria-controls={open ? 'account-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                sx={(theme) => ({
                  p: '2px',
                  '&:focus-visible': headerFocusRing(theme),
                })}
              >
                <Avatar
                  sx={(theme) => ({
                    width: 32,
                    height: 32,
                    bgcolor: theme.palette.common.white,
                    color: getHeaderBackground(theme),
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    textTransform: 'uppercase',
                    boxShadow: `0 0 0 2px ${alpha(
                      theme.palette.common.white,
                      0.24,
                    )}`,
                  })}
                >
                  {data.first_name.charAt(0)}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
        </>
      )}

      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          elevation: 4,
          sx: {
            mt: 1,
            minWidth: 200,
            borderRadius: '10px',
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
          },
        }}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
      >
        <MenuItem onClick={handleAccountClick}>
          <Avatar /> My account
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => navigate('/administrator')}>
          <ListItemIcon>
            <AdminPanelSettingsOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Administrator
        </MenuItem>
        <MenuItem>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogOut fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default DashboardShellHeader;
